import {getQueryClient} from '@github-ui/query-client'
import {applyOperation} from '@github-ui/optimistic-connection'
import type {IssueBodyRefetchServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueBodyRefetchServerQuery'
import type {IssueHeaderRefetchServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueHeaderRefetchServerQuery'
import type {IssueMetadataRefetchServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueMetadataRefetchServerQuery'
import type {IssueTimelineSnapshotQuery} from '@github-ui/server-persisted-queries/types/IssueTimelineSnapshot'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'

import {
  type IssueViewerQueryData,
  type IssueViewerQueryIdentifier,
  issueViewerPrimaryQueryOptions,
  persistIssueViewerPrimary,
} from './issue-viewer-query'

type IssueHeaderRefetchIssue = Extract<
  NonNullable<IssueHeaderRefetchServerQueryQuery['node']>,
  {readonly __typename: 'Issue'}
>

export type IssueViewerHeaderProjection = IssueHeaderRefetchIssue

export type IssueViewerPrimaryIssue = NonNullable<
  NonNullable<NonNullable<IssueViewerQueryData['payload']['data']>['repository']>['issue']
>

export type IssueViewerMetadataProjection = Extract<
  NonNullable<IssueMetadataRefetchServerQueryQuery['node']>,
  {readonly __typename: 'Issue'}
>

export type IssueViewerBodyProjection = Extract<
  NonNullable<IssueBodyRefetchServerQueryQuery['node']>,
  {readonly __typename: 'Issue'}
>

export type IssueViewerSecondaryProjection = NonNullable<
  NonNullable<IssueViewerSecondaryViewServerQueryQuery['repository']>['issue']
>

export type IssueViewerTimelineSnapshotProjection = NonNullable<
  NonNullable<IssueTimelineSnapshotQuery['repository']>['issue']
>

export type IssueViewerPrimaryProjector = (data: IssueViewerQueryData) => IssueViewerQueryData

export function projectIssueViewerHeader(
  data: IssueViewerQueryData,
  header: IssueViewerHeaderProjection,
): IssueViewerQueryData {
  return projectIssueViewerFields(data, header)
}

export function projectIssueViewerMetadata(
  data: IssueViewerQueryData,
  metadata: IssueViewerMetadataProjection,
): IssueViewerQueryData {
  return projectIssueViewerFields(data, metadata)
}

export function projectIssueViewerBody(
  data: IssueViewerQueryData,
  body: IssueViewerBodyProjection,
): IssueViewerQueryData {
  return projectIssueViewerFields(data, body)
}

export function projectIssueViewerSecondary(
  data: IssueViewerQueryData,
  secondary: IssueViewerSecondaryProjection,
): IssueViewerQueryData {
  return projectIssueViewerFields(data, secondary)
}

export function projectIssueViewerTimelineSnapshot(
  data: IssueViewerQueryData,
  snapshot: IssueViewerTimelineSnapshotProjection,
): IssueViewerQueryData {
  return projectIssueViewerFields(data, snapshot)
}

export function projectIssueViewerPageTitle(data: IssueViewerQueryData, title: string): IssueViewerQueryData {
  if (data.meta?.title === title) return data
  return {...data, meta: {...data.meta, title}}
}

export function projectIssueViewerFields<T extends {readonly id: string}>(
  data: IssueViewerQueryData,
  fields: T,
): IssueViewerQueryData {
  const payloadData = data.payload.data
  const repository = payloadData?.repository
  const issue = repository?.issue
  if (!payloadData || !repository || !issue || issue.id !== fields.id) return data

  const projectedIssue = applyOperation(issue, fields.id, {
    type: 'setFields',
    id: fields.id,
    fields: Object.fromEntries(Object.entries(fields)),
  })
  if (projectedIssue === issue) return data

  return {
    ...data,
    payload: {
      ...data.payload,
      data: {
        ...payloadData,
        repository: {
          ...repository,
          issue: projectedIssue,
        },
      },
    },
  }
}

export async function reconcileIssueViewerPrimary({
  identifier,
  issueId,
  project,
  queryClient = getQueryClient(),
}: {
  identifier: IssueViewerQueryIdentifier
  issueId: string
  project: IssueViewerPrimaryProjector
  queryClient?: ReturnType<typeof getQueryClient>
}): Promise<boolean> {
  const {queryKey} = issueViewerPrimaryQueryOptions(identifier)
  const existing = queryClient.getQueryCache().find({queryKey, exact: true})
  if (!existing?.state.data) return false

  await queryClient.cancelQueries({queryKey, exact: true}, {silent: true})

  const state = queryClient.getQueryState<IssueViewerQueryData>(queryKey)
  const data = state?.data
  if (!data || data.payload.data?.repository?.issue?.id !== issueId) return false

  const projected = project(data)
  if (projected === data) return false

  queryClient.setQueryData(queryKey, projected, {updatedAt: state.dataUpdatedAt})
  await persistIssueViewerPrimary(identifier, queryClient)
  return true
}
